import { MutableRefObject, useCallback, useEffect, useRef, useState } from 'react';
import { Gimbal } from '@/lib/gimbal';
import { OrientationPermissionState } from '@/lib/types';

type DeviceOrientation = {
    alpha: number | null;
    beta: number | null;
    gamma: number | null;
};

type UseDeviceOrientationData = {
    orientation: MutableRefObject<DeviceOrientation | null>;
    orientationPermission: OrientationPermissionState | undefined;
    requestAccess: () => Promise<boolean>;
    revokeAccess: () => Promise<void>;
    reOrient: () => void;
};

const gimbal = new Gimbal();

export const useDeviceOrientation = (): UseDeviceOrientationData => {
    const orientation = useRef<DeviceOrientation | null>(null);
    const [access, setAccess] = useState<boolean>(false);
    const [permissionState, setPermissionState] = useState<OrientationPermissionState>();

    useEffect(() => {
        if (access) {
            const onDeviceOrientation = (): void => {
                gimbal.update();
                orientation.current = {
                    alpha: gimbal.roll || null,
                    beta: gimbal.pitch || null,
                    gamma: gimbal.yaw || null,
                };
            };
            window.addEventListener('deviceorientation', onDeviceOrientation);
            return () => window.removeEventListener('deviceorientation', onDeviceOrientation);
        }
    }, [access]);

    const revokeAccess = useCallback(async (): Promise<void> => {
        gimbal.disable();
        setAccess(false);
        orientation.current = null;
    }, []);

    const requestAccess = useCallback(async (): Promise<boolean> => {
        if (!window.DeviceOrientationEvent) {
            console.log('Device orientation event is not supported by your browser');
            setPermissionState('notasked');
            return false;
        }

        if (
            (DeviceOrientationEvent as any).requestPermission &&
            typeof (DeviceMotionEvent as any).requestPermission === 'function'
        ) {
            let permission: PermissionState;
            try {
                permission = await (DeviceOrientationEvent as any).requestPermission();
                setPermissionState(permission);
            } catch (err: any) {
                console.log(err);
                setPermissionState('notasked');
                return false;
            }
            if (permission !== 'granted') {
                console.log('Request to access the device orientation was rejected');
                return false;
            }
        }

        gimbal.enable();
        gimbal.recalRequested = true;
        setAccess(true);

        return true;
    }, []);

    const reOrient = useCallback(() => (gimbal.recalRequested = true), []);

    // requesting access on load doesn't prompt the user to grant permission, a user interaction
    // is required to trigger the prompt, instead, it can tell us if the user has previously
    // granted or denied permission
    useEffect(() => {
        void requestAccess();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    return {
        orientation,
        orientationPermission: permissionState,
        requestAccess,
        revokeAccess,
        reOrient,
    };
};
